import React, { FC, memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { useButton } from '~Hooks';
import { Locale } from '~Types';

/** Flag and name in a small pill. Active: white fill, dark text. Inactive: just white text. */
const Pill = styled.button`
  appearance: none;
  border: none;
  height: 40px;
  padding: 0 14px 0 8px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: var(--eposFontFamily);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.2px;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  transition: background-color 0.2s ease-out, color 0.2s ease-out, transform 0.15s ease-out;

  &.is-active {
    background: var(--colorWhite);
    color: var(--colorBlueGray1300);
    font-weight: 700;
  }

  &.is-touching {
    transform: scale(0.95);
  }
`;

const Flag = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 999px;
`;

interface Props {
  code: Locale;
  title: string;
  icon: string;
  active: boolean;
  onSelect: (code: Locale) => void;
}

const LanguageButton: FC<Props> = ({ code, title, icon, active, onSelect }) => {
  const handleClick = useCallback(() => onSelect(code), [onSelect, code]);
  const { touching, disabled, disabledToast, ...button } = useButton({
    onClick: handleClick,
    disabled: active,
  });

  const className = [active && 'is-active', touching && !active && 'is-touching']
    .filter(Boolean)
    .join(' ');

  return (
    <Pill
      {...button}
      className={className || undefined}
      aria-pressed={active}
      data-testid={`epos-language-${code}`}>
      <Flag src={icon} alt="" />
      {title}
    </Pill>
  );
};

export default memo(LanguageButton);
